/**
 * Dependencies as a FIELD, beside Tags — a trigger that reads the count, and a
 * popover holding the editor.
 *
 * It used to be a full-width band of its own between Tags and Documents, which
 * gave a list most issues do not have the same weight as the comment thread.
 * As a field it says what there is in one line and gets out of the way; the
 * whole section is one click behind it.
 */
import { useMemo, useRef, useState } from "react";
import { GitBranch } from "lucide-react";

import { cn } from "@/lib/utils";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIssueDependencies } from "@/domains/pm/hooks/useIssueDependencies";
import { useScheduleStaging } from "@/domains/pm/components/issues/schedule/scheduleStagingContext";
import { overlayDependencies } from "@/domains/pm/hooks/useStagedSchedule";
import type { PMIssue } from "@/domains/pm/types";
import { IssueDependenciesSection } from "../IssueDependenciesSection";
import { panelEdgeRect } from "./panelEdgeAnchor";

export function DependencyField({
  issue,
  readOnly = false,
}: {
  issue: PMIssue;
  readOnly?: boolean;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.dependencies;
  const { dependencies } = useIssueDependencies(issue.building_id);
  const staging = useScheduleStaging();
  const [open, setOpen] = useState(false);
  // The add dialog is portalled out of this popover; while it is open, focus
  // and clicks inside it are "outside" here and would close the popover —
  // unmounting the dialog with it.
  const [pickerOpen, setPickerOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Measured on every call — Radix re-reads it as the panel scrolls or resizes.
  const anchorRef = useRef({
    getBoundingClientRect: () =>
      triggerRef.current ? panelEdgeRect(triggerRef.current) : new DOMRect(),
  });
  const holdWhilePicking = (e: Event) => {
    if (pickerOpen) e.preventDefault();
  };

  const { waiting, drives } = useMemo(() => {
    const all = staging ? overlayDependencies(dependencies.all, staging.staged) : dependencies.all;
    return {
      waiting: all.filter((d) => d.successor_issue_id === issue.id).length,
      drives: all.filter((d) => d.predecessor_issue_id === issue.id).length,
    };
  }, [dependencies, staging, issue.id]);

  const summary =
    waiting + drives === 0
      ? copy.none
      : [
          waiting > 0 && fill(copy.waitingCount, { count: String(waiting) }),
          drives > 0 && fill(copy.drivesCount, { count: String(drives) }),
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    // Modal, so it carries its own scroll lock. In the 3D viewer and Drawing
    // Compare this panel sits inside a Radix Dialog whose lock blocks wheel and
    // touch scrolling in anything portalled outside it — which this popover
    // is. react-remove-scroll only enforces the topmost lock, and a modal
    // popover's lock allows its own content. The add dialog stacks above it
    // the same way, with its own lock and focus scope.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            "-ml-1.5 flex h-8 items-center gap-1.5 rounded-md px-1.5 text-sm hover:bg-accent",
            waiting + drives === 0 && "text-muted-foreground"
          )}
        >
          <GitBranch className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {summary}
        </button>
      </PopoverTrigger>
      <PopoverContent
        // Beside the panel rather than under the field: below, the editor ran
        // off the bottom of the screen with nothing to scroll. The height is
        // capped to the space Radix measures, and the content scrolls inside.
        // The anchor spans the panel, so either side stays outside it (see
        // panelEdgeAnchor).
        side="left"
        align="start"
        sideOffset={12}
        collisionPadding={16}
        className="max-h-[var(--radix-popover-content-available-height)] w-[30rem] max-w-[calc(100vw-2rem)] overflow-y-auto overscroll-contain p-4"
        onInteractOutside={holdWhilePicking}
        onFocusOutside={holdWhilePicking}
      >
        <IssueDependenciesSection
          issue={issue}
          readOnly={readOnly}
          hideHeading
          onPickerOpenChange={setPickerOpen}
        />
      </PopoverContent>
    </Popover>
  );
}
